import { ArrowRight, MapPin, Leaf, Bug, Star } from "lucide-react";
import { Button } from "./ui/button";
import { useT } from "../lib/i18n";

export default function Hero() {
  const { t } = useT();
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-50 blur-3xl animate-blob" />
        <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E8F5E9] opacity-70 blur-3xl animate-blob delay-200" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-[#A5D6A7] opacity-30 blur-3xl animate-blob delay-400" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EBE5] shadow-sm mb-6">
              <MapPin className="w-4 h-4 text-[#1B5E20]" />
              <span className="text-sm font-medium text-[#1A2E1C]">
                {t("hero.zone")}
              </span>
            </div>

            {/* Google reviews badge — social proof */}
            <a
              href="https://www.google.com/search?q=K%27REUSE+EXT%C3%89RIEUR+La+Celle-Dunoise#lrd"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-reviews-badge"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#FBBC04]/40 shadow-sm mb-6 ml-2 hover:shadow-md transition-shadow"
            >
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={`hero-star-${i}`} className="w-3.5 h-3.5 fill-[#FBBC04] text-[#FBBC04]" />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#1A2E1C]">5,0</span>
              <span className="text-xs text-[#5C705E]">· 25 avis Google</span>
            </a>

            <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
              {t("hero.title1")}{" "}
              <span className="font-script text-[#1B5E20] font-semibold inline-block -rotate-2">
                {t("hero.title2")}
              </span>
            </h1>

            <p className="mt-7 text-lg md:text-xl text-[#5C705E] max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="hero-cta-primary"
                className="bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full px-8 py-6 text-base font-medium group transition-all hover:-translate-y-0.5"
              >
                <a href="#contact">
                  {t("hero.ctaPrimary")}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-secondary"
                className="bg-white border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-8 py-6 text-base font-medium"
              >
                <a href="#services">{t("hero.ctaSecondary")}</a>
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap gap-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-[#1B5E20]" />
                </div>
                <div>
                  <div className="font-['Outfit'] font-semibold text-[#1A2E1C]">
                    {t("hero.badge1Title")}
                  </div>
                  <div className="text-sm text-[#5C705E]">{t("hero.badge1Sub")}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  <Bug className="w-5 h-5 text-[#1B5E20]" />
                </div>
                <div>
                  <div className="font-['Outfit'] font-semibold text-[#1A2E1C]">
                    {t("hero.badge2Title")}
                  </div>
                  <div className="text-sm text-[#5C705E]">
                    {t("hero.badge2Sub")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up delay-200">
            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl">
              <img
                src="/img/photos/jardin-paysager.webp"
                alt="Jardin paysager en Creuse aménagé par K'REUSE EXTÉRIEUR — création et entretien de jardins"
                width="1600"
                height="1067"
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B5E20]/30 via-transparent to-transparent" />
            </div>

            {/* Floating card */}
            <div className="hidden md:block absolute -left-8 bottom-8 bg-white rounded-2xl p-5 shadow-xl border border-[#E5EBE5] animate-float max-w-[240px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#1B5E20] flex items-center justify-center text-white font-bold text-lg">
                  ✓
                </div>
                <div className="font-['Outfit'] font-semibold text-[#1A2E1C]">
                  {t("hero.floatBadge")}
                </div>
              </div>
              <p className="text-sm text-[#5C705E]">
                {t("hero.floatBadgeSub")}
              </p>
            </div>

            <div className="hidden md:block absolute -right-4 top-12 bg-[#1B5E20] text-white rounded-2xl px-5 py-4 shadow-xl animate-float delay-300">
              <div className="text-3xl font-['Outfit'] font-bold">100%</div>
              <div className="text-sm opacity-90">{t("hero.satisfaction")}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
